/**
 * Shared stats card grid — replaces the duplicated 4/5-card stat patterns
 * across DODD, AZAI, BH, Employees, Agency, etc.
 *
 * Usage:
 *   <StatsCardGrid
 *     columns={4}
 *     cards={[
 *       { label: 'Total', value: 234, icon: FileText, color: 'indigo', testid: 'stat-total' },
 *       { label: 'Pending', value: 12, icon: Clock, color: 'amber', filter: 'pending', testid: 'stat-pending' },
 *       { label: 'Revenue', value: 12500, icon: CurrencyDollar, color: 'emerald', isCurrency: true, testid: 'stat-rev' },
 *     ]}
 *     activeFilter={filterStatus}
 *     onFilterChange={setFilterStatus}
 *   />
 *
 * Each `card` prop:
 *   - label (string)        — small caption above the value
 *   - value (number|string) — the big number/text
 *   - icon (component)      — Phosphor icon (or any component accepting size/className)
 *   - color (string)        — one of: indigo, amber, blue, green, emerald, red, purple, slate, teal, sky, rose, violet
 *   - filter (string?)      — optional filter value; clicking the card calls onFilterChange(filter)
 *   - isCurrency (bool?)    — formats value as USD when true
 *   - suffix (string?)      — appended to value (e.g. '%')
 *   - testid (string?)      — data-testid override (defaults to `stat-<label-slug>`)
 */
import React, { useMemo } from 'react';
import { Card, CardContent } from '../ui/card';

// Static color map — Tailwind can't compile dynamic `bg-${x}-50` strings reliably,
// so we keep the full class names here and look them up.
const COLOR_MAP = {
  indigo: { iconBg: 'bg-indigo-50', iconText: 'text-indigo-600', ring: 'ring-indigo-200', hover: 'hover:border-indigo-300' },
  amber: { iconBg: 'bg-amber-50', iconText: 'text-amber-600', ring: 'ring-amber-200', hover: 'hover:border-amber-300' },
  blue: { iconBg: 'bg-blue-50', iconText: 'text-blue-600', ring: 'ring-blue-200', hover: 'hover:border-blue-300' },
  green: { iconBg: 'bg-green-50', iconText: 'text-green-600', ring: 'ring-green-200', hover: 'hover:border-green-300' },
  emerald: { iconBg: 'bg-emerald-50', iconText: 'text-emerald-600', ring: 'ring-emerald-200', hover: 'hover:border-emerald-300' },
  red: { iconBg: 'bg-red-50', iconText: 'text-red-600', ring: 'ring-red-200', hover: 'hover:border-red-300' },
  purple: { iconBg: 'bg-purple-50', iconText: 'text-purple-600', ring: 'ring-purple-200', hover: 'hover:border-purple-300' },
  slate: { iconBg: 'bg-slate-100', iconText: 'text-slate-600', ring: 'ring-slate-200', hover: 'hover:border-slate-300' },
  teal: { iconBg: 'bg-teal-50', iconText: 'text-teal-600', ring: 'ring-teal-200', hover: 'hover:border-teal-300' },
  sky: { iconBg: 'bg-sky-50', iconText: 'text-sky-600', ring: 'ring-sky-200', hover: 'hover:border-sky-300' },
  rose: { iconBg: 'bg-rose-50', iconText: 'text-rose-600', ring: 'ring-rose-200', hover: 'hover:border-rose-300' },
  violet: { iconBg: 'bg-violet-50', iconText: 'text-violet-600', ring: 'ring-violet-200', hover: 'hover:border-violet-300' },
};

const COLUMN_CLASS = {
  2: 'grid-cols-1 md:grid-cols-2',
  3: 'grid-cols-1 md:grid-cols-3',
  4: 'grid-cols-1 sm:grid-cols-2 md:grid-cols-4',
  5: 'grid-cols-2 md:grid-cols-3 lg:grid-cols-5',
  6: 'grid-cols-2 md:grid-cols-3 lg:grid-cols-6',
};

const slug = (s) => String(s).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

const formatCurrency = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);

export const StatsCardGrid = ({ cards, columns = 4, activeFilter, onFilterChange, className = '' }) => {
  const gridClass = COLUMN_CLASS[columns] || COLUMN_CLASS[4];

  // Pre-compute per-card derived values so each row only changes when its
  // own props change, instead of recomputing on every parent render.
  const computedCards = useMemo(
    () =>
      cards.map((card) => {
        const colors = COLOR_MAP[card.color] || COLOR_MAP.slate;
        const clickable = card.filter !== undefined && Boolean(onFilterChange);
        const active = clickable && activeFilter === card.filter;
        const display = card.isCurrency
          ? formatCurrency(card.value)
          : `${card.value ?? 0}${card.suffix || ''}`;
        const testid = card.testid || `stat-${slug(card.label)}`;
        const className = [
          'border-slate-200 transition-colors',
          clickable ? `cursor-pointer ${colors.hover}` : '',
          active ? `ring-2 ${colors.ring}` : '',
        ]
          .filter(Boolean)
          .join(' ');
        return { ...card, colors, clickable, active, display, testid, className };
      }),
    [cards, activeFilter, onFilterChange]
  );

  return (
    <div className={`grid ${gridClass} gap-4 ${className}`}>
      {computedCards.map((card) => {
        const Icon = card.icon;
        return (
          <Card
            key={card.testid}
            className={card.className}
            onClick={card.clickable ? () => onFilterChange(card.filter) : undefined}
            data-testid={card.testid}
          >
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                {Icon && (
                  <div className={`p-2 ${card.colors.iconBg} rounded-lg shrink-0`}>
                    <Icon size={24} className={card.colors.iconText} />
                  </div>
                )}
                <div className="min-w-0">
                  <p className="text-xs text-slate-500 truncate">{card.label}</p>
                  <p className={`text-2xl font-bold text-slate-900 ${card.isCurrency ? 'font-mono' : ''}`}>
                    {card.display}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};
